import { apiDownload, apiGet, apiPost } from '../../lib/api'
import { withAuth } from '../auth/token'
import type { TruckAvailability } from '../availability/api'
import type { ComplianceState } from '../brokers/api'

/** Mirrors the broker broadcast slices on the API (Stage 2, 2.4). */

/**
 * How far this environment's mail goes. Both environments run Recording for now (Lino,
 * 2026-09-30): every message is composed and kept, and nothing is sent.
 */
export type MailMode = 'Disabled' | 'Recording' | 'Allowlist' | 'Live'

export type DeliveryStatus = 'Queued' | 'Sending' | 'Recorded' | 'Sent' | 'Suppressed' | 'Failed'

export type SuppressionReason =
  | 'NotOnAllowlist'
  | 'AgentOptedOut'
  | 'AgentStoodDown'
  | 'BrokerStoodDown'
  | 'BrokerBlocked'
  | 'PostingWithdrawn'
  | 'PostingExpired'
  | 'UnitHeldBack'
  | 'SameAddressAsAnother'

export type FailureKind = 'Rejected' | 'Unavailable' | 'Interrupted'

/** Why an agent cannot receive this posting's broadcast — the review screen's reason per row. */
export type RecipientReason =
  | 'opted_out'
  | 'agent_stood_down'
  | 'broker_stood_down'
  | 'broker_blocked'
  | 'already_sent'
  | 'uncertain'
  | 'in_progress'

/** The message as it reads — the same for every recipient but the To line. */
export interface BroadcastMessage {
  fromAddress: string
  fromName: string
  replyTo: string
  subject: string
  textBody: string
  htmlBody: string
}

export interface DraftAgent {
  agentId: string
  name: string
  title: string | null
  email: string
  eligible: boolean
  reason: RecipientReason | null
  /** With `already_sent` and `uncertain`: when this posting last went, or may have gone, to the agent. */
  lastSentAt: string | null
  /**
   * Whether we still work with the agent. The reason names only the first thing that holds an
   * agent back — an inactive agent of a Blocked broker reads `broker_blocked` — so this says it.
   */
  active: boolean
}

export interface DraftBroker {
  brokerId: string
  name: string
  complianceState: ComplianceState
  active: boolean
  agents: DraftAgent[]
}

export interface BroadcastDraft {
  mode: MailMode
  /** The mode processes mail, the posting is open and every unit on it can be offered. */
  canSend: boolean
  reference: string
  posting: TruckAvailability
  brokers: DraftBroker[]
  message: BroadcastMessage
  /** The version of `message`, which the send hands back: an email nobody read never goes. */
  messageVersion: string
}

export interface BroadcastCounts {
  queued: number
  sending: number
  recorded: number
  sent: number
  suppressed: number
  failed: number
}

export interface BroadcastSummary {
  id: string
  postingId: string
  reference: string
  mode: MailMode
  sentByEmail: string | null
  createdAt: string
  total: number
  counts: BroadcastCounts
}

export interface BroadcastRecipient {
  id: string
  sequence: number
  agentId: string
  brokerId: string
  agentName: string
  brokerName: string
  address: string
  status: DeliveryStatus
  attempts: number
  processedAt: string | null
  providerMessageId: string | null
  suppressedBecause: SuppressionReason | null
  failure: FailureKind | null
  /** The provider's own English words about a refusal, shown beside the translated label. */
  failureDetail: string | null
  /** Composed, so it downloads as an `.eml`. */
  hasMessage: boolean
}

export interface BroadcastDetail {
  id: string
  postingId: string
  postingNumber: number
  reference: string
  /** The mode it was sent under. */
  mode: MailMode
  /** The environment's mode now: what is still queued goes out under the narrower of the two. */
  environmentMode: MailMode
  includesMinimumRate: boolean
  sentByEmail: string | null
  createdAt: string
  message: BroadcastMessage
  counts: BroadcastCounts
  recipients: BroadcastRecipient[]
}

export interface SendBroadcastPayload {
  agentIds: string[]
  includeMinimumRate: boolean
  /** The version of the email on screen — the send refuses one that would no longer read so. */
  messageVersion: string
}

export function getBroadcastDraft(postingId: string, includeMinimumRate: boolean): Promise<BroadcastDraft> {
  return apiGet<BroadcastDraft>(
    `/api/availability/${postingId}/broadcast-draft?includeMinimumRate=${includeMinimumRate ? 'true' : 'false'}`,
    { headers: withAuth() },
  )
}

export function sendBroadcast(postingId: string, payload: SendBroadcastPayload): Promise<BroadcastDetail> {
  return apiPost<BroadcastDetail>(`/api/availability/${postingId}/broadcasts`, payload, { headers: withAuth() })
}

export function listBroadcasts(postingId: string): Promise<BroadcastSummary[]> {
  return apiGet<BroadcastSummary[]>(`/api/availability/${postingId}/broadcasts`, { headers: withAuth() })
}

export function getBroadcast(broadcastId: string): Promise<BroadcastDetail> {
  return apiGet<BroadcastDetail>(`/api/broadcasts/${broadcastId}`, { headers: withAuth() })
}

/** Whether anything on a broadcast is still waiting for the drain. */
export function isInFlight(counts: BroadcastCounts): boolean {
  return counts.queued + counts.sending > 0
}

/** "TC-A-12 to Jane Doe.eml" — the name the API gives the file, built from what the row already says. */
export function emlFileName(reference: string, agentName: string): string {
  const name = agentName.replace(/[\\/:*?"<>|]/g, '').trim()
  return name === '' ? `${reference}.eml` : `${reference} to ${name}.eml`
}

/**
 * One recipient's message, handed to the browser as an `.eml` — the file a mail client opens
 * as if it had arrived. A plain link cannot carry the bearer token, so the bytes come back
 * first (the documents panel's way).
 */
export async function downloadBroadcastMessage(
  broadcast: { id: string; reference: string },
  recipient: { id: string; agentName: string },
): Promise<void> {
  const blob = await apiDownload(`/api/broadcasts/${broadcast.id}/recipients/${recipient.id}/message`, {
    headers: withAuth(),
  })

  const url = URL.createObjectURL(blob)
  try {
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = emlFileName(broadcast.reference, recipient.agentName)
    anchor.click()
  } finally {
    URL.revokeObjectURL(url)
  }
}
